import { Address } from "@/dto/address";
import { useEffect, useRef, useState } from "react";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { useSearchParams } from "next/navigation";
import { OrderType } from "@/types";
import { MerchantLogo } from "./merchantLogo";
import { MerchantNameAndAddress } from "./merchantNameAndAddress";

export interface HeaderMerchantInfoContainerProps {
  merchantLogo?: string;
  merchantName?: string;
  isSimpleStorefront: boolean;
  isStandaloneGiftCard: boolean;
  merchantAddress: Address;
  merchantPhone: string;
}

export const HeaderMerchantInfoContainer = ({
  merchantLogo,
  merchantName,
  isSimpleStorefront,
  isStandaloneGiftCard,
  merchantAddress,
  merchantPhone,
}: HeaderMerchantInfoContainerProps) => {
  const [isMultiline, setIsMultiline] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const isDineIn =
    searchParams?.get("order_type")?.toLocaleLowerCase() ===
    OrderType.DINE_IN.toLocaleLowerCase();

  const showLink = !isDineIn && !isStandaloneGiftCard;
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const scanToOrderInfo = getFromLocalStorage<ScanToOrderInfo | null>(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    null
  );
  const pathname = "/";
  const queryParams = {
    order_type: scanToOrderInfo?.orderType,
    table_id: scanToOrderInfo?.tableId,
    employee_id: scanToOrderInfo?.employeeId,
  };
  const linkHref = {
    pathname: pathname,
    ...(scanToOrderInfo && { query: queryParams }),
  };

  useEffect(() => {
    const checkMultiline = () => {
      if (textRef.current) {
        const element = textRef.current;
        const style = window.getComputedStyle(element);
        const lineHeight = parseInt(style.lineHeight, 10);
        const elementHeight = element.clientHeight;
        setIsMultiline(elementHeight > lineHeight);
      }
    };

    checkMultiline();

    window.addEventListener("resize", checkMultiline);

    return () => {
      window.removeEventListener("resize", checkMultiline);
    };
  }, []);

  return (
    <div
      className={[
        "flex",
        `${isSimpleStorefront && "flex-col"}`,
        "sm:flex-row",
        "gap-[22px]",
        "sm:gap-6",
        "items-center",
      ].join(" ")}
    >
      <MerchantLogo
        merchantLogo={merchantLogo}
        showLink={showLink}
        linkHref={linkHref}
      />
      <MerchantNameAndAddress
        showLink={showLink}
        isSimpleStorefront={isSimpleStorefront}
        isStandaloneGiftCard={isStandaloneGiftCard}
        linkHref={linkHref}
        merchantAddress={merchantAddress}
        merchantPhone={merchantPhone}
        textRef={textRef}
        merchantName={merchantName}
        isMultiline={isMultiline}
      />
    </div>
  );
};
